import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useGetCompanyTimeAttendanceStatsQuery } from "@/services/(company)/api.time-attendance";
import { useGetPendingApprovalsQuery } from "@/services/(company)/attendanceRegularizationApi";
import { useLocale } from "@/hooks/useLocale";
import {
  HeroLine,
  MetricValue,
  MiniMetric,
  MiniStat,
  ProgressTrack,
  WidgetNoAccess,
} from "./primitives";

/**
 * The regularization backlog, led by whoever has been waiting longest in it.
 *
 * This was six counts at four sizes — the backlog, late check-ins, early
 * check-outs — and none of them told an approver what to do next. The head of
 * the queue does, so from `md` up it is the headline and the count demotes to
 * context beside it.
 *
 * The late-check-in figure is drawn against today's attendance rather than
 * standing alone: eight late arrivals means nothing until you know whether eight
 * or eighty people came in. Late check-ins are a strict subset of check-ins —
 * you cannot be late without arriving — so that fraction is honest. Early
 * check-outs deliberately stay a bare count: adding them to the numerator would
 * double-count anyone who was both late in and early out.
 */
export const RegularizationWidget = ({ size }: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  const { locale } = useLocale();
  // `addSuffix: false`, not `useLocale().formatRelative`: "waiting" already
  // supplies the direction, so a helper that appends "ago" would read
  // "waiting 3 days ago".
  const formatWaitingDuration = (date: string | Date) =>
    formatDistanceToNow(new Date(date), {
      addSuffix: false,
      locale: locale.dateFnsLocale,
    });
  const { data, isLoading, error } = useGetCompanyTimeAttendanceStatsQuery({});

  // Same endpoint and same args the Pending approvals widget uses, so RTK Query
  // serves this from cache when both are on the canvas and fetches once when
  // only this one is. Skipped at the two sizes with no room for a queue head.
  const needsQueueHead = size !== "xs" && size !== "sm";
  const {
    data: approvals,
    isLoading: isApprovalsLoading,
    error: approvalsError,
  } = useGetPendingApprovalsQuery({ limit: 8 }, { skip: !needsQueueHead });

  // Every figure on this widget comes from the attendance stats, so losing them
  // leaves nothing to draw.
  if (error) return <WidgetNoAccess error={error} />;

  // The queue head is enrichment from the regularization worklist. Losing only
  // that keeps every count and simply stops naming who is at the front.
  const noQueueHead = Boolean(approvalsError);
  const stats = data?.data;
  const pending = stats?.tabCounts?.regularizationRequestCount ?? 0;
  const present = stats?.totalPresent ?? 0;
  const lateCheckIn = stats?.totalLateCheckIn ?? 0;

  // Most-recent-first, so the oldest of the loaded page is its last entry — a
  // floor on the true wait rather than the exact one, since an older request
  // could sit beyond the page. Understating reads as less urgent than reality,
  // never more, which is the safe direction to be wrong in.
  const regularizations = (approvals?.data ?? []).filter(
    (item) => item.type === "REGULARIZATION",
  );
  const oldest = regularizations.at(-1);

  // The card draws the chip and the title at every size, this one included, so
  // there is nothing here to add beyond the number.
  if (size === "xs") {
    return <MiniMetric value={pending} isLoading={isLoading} tone="warning" />;
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-0.5">
        <MetricValue value={pending} isLoading={isLoading} tone="warning" />
        <p className="text-[11px] text-muted-foreground">
          {t("company:widgets.open_requests")}
        </p>
      </div>
    );
  }

  const queueHead = (
    <HeroLine
      isLoading={isApprovalsLoading}
      avatar={oldest && { name: oldest.fullName, image: oldest.profileImage }}
      // With the worklist forbidden there is no head to name, and the
      // "Nothing to regularize" copy would contradict the count beside it.
      // Kept short deliberately — `HeroLine` truncates both lines with no
      // `title` fallback, and this sits in a card as narrow as ~90px.
      primary={
        oldest?.fullName ??
        (noQueueHead
          ? t("company:widgets.no_access")
          : t("company:widgets.nothing_to_regularize"))
      }
      caption={
        oldest
          ? t("company:widgets.waiting_duration", {
              duration: formatWaitingDuration(oldest.createdAt),
            })
          : noQueueHead
            ? t("company:widgets.not_visible_to_you")
            : t("company:widgets.no_open_requests")
      }
      // Loading is its own state, distinct from a genuine zero: without it, the
      // figure vanished during the load window the same way `pending` does
      // before the stats resolve, and "Nothing to regularize" briefly asserted
      // a backlog that simply hadn't loaded yet.
      trailing={
        isLoading || pending > 0 ? (
          <MetricValue
            value={pending}
            isLoading={isLoading}
            tone="warning"
            scale="md"
          />
        ) : undefined
      }
    />
  );

  // Narrow and two rows tall: the queue head, then the figures stacked in a
  // column no wider than it.
  if (size === "tall") {
    return (
      <div className="flex h-full flex-col justify-between gap-2">
        {queueHead}
        <div className="flex flex-col gap-2 border-t border-border pt-2">
          <ProgressTrack
            label={t("company:widgets.checked_in_late")}
            value={lateCheckIn}
            total={present}
            tone="warning"
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:widgets.early_check_out")}
            tone="warning"
            value={stats?.totalEarlyCheckOut ?? 0}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  // Wide and one row tall — there is width for two things side by side but no
  // height to stack them, so the queue head and the track share the row rather
  // than one of them being dropped.
  return (
    <div className="flex h-full items-center gap-4">
      <div className="min-w-0 flex-1">{queueHead}</div>
      <div className="min-w-0 flex-1 border-s border-border ps-4">
        <ProgressTrack
          label={t("company:widgets.checked_in_late")}
          value={lateCheckIn}
          total={present}
          tone="warning"
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
